// src/components/AppHeader.js
import React, { useState } from "react";
import { useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import Badge from "@mui/material/Badge";
import NotificationsIcon from "@mui/icons-material/Notifications";
import MenuIcon from "@mui/icons-material/Menu";
import HomeIcon from "@mui/icons-material/Home";
import LogoutIcon from "@mui/icons-material/Logout";
import WarningIcon from "@mui/icons-material/Warning";
import HistoryIcon from "@mui/icons-material/History";
import { useNavigate } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import axios from "axios";

const AppHeader = ({ setIsLoggedIn }) => {
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/web-logout`,
        {},
        {
          withCredentials: true,
          headers: {
            "x-tenant": localStorage.getItem("tenant") || ""
          }
        }
      );
    } catch (err) {
      console.error("Logout API Error:", err);
    }

    sessionStorage.removeItem("user");  // clear user session
    sessionStorage.removeItem("session_valid");
    if (setIsLoggedIn) { setIsLoggedIn(false); }
    window.location.href = "/login";
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const [setupOpen, setSetupOpen] = useState(false);

  const [pendingCount, setPendingCount] = useState(0);

  const [userDisplay, setUserDisplay] = useState('');
  const [tenantDisplay, setTenantDisplay] = useState('');

  const fetchPendingCount = async () => {
    try {
      const tenant = localStorage.getItem("tenant") || "";
      const res = await axios.get(`${process.env.REACT_APP_API_URL1}/approval/inbox/count`, {
        withCredentials: true,
        headers: { "X-Tenant": tenant }
      });
      if (res.data?.success) setPendingCount(res.data.count || 0);
    } catch (err) {
      console.error("Fetch pending count error:", err);
    }
  };

  React.useEffect(() => {
    fetchPendingCount();
    const timer = setInterval(fetchPendingCount, 60000);
    return () => clearInterval(timer);
  }, []);

  const [role, setRole] = useState(null);

  useEffect(() => {
    const storedUser = sessionStorage.getItem("user");

    const tenant = localStorage.getItem("tenant") || "";
    setTenantDisplay(tenant);

    if (storedUser) {
      const u = JSON.parse(storedUser);
      setRole(u.role);
      const name = u.full_name || u.fullName || u.username || u.email || u.name || '';
      setUserDisplay(name);
      return;
    }

    // Fallback: restore session from backend (cookie-based login)
    const restoreSession = async () => {
      try {
        const tenant = localStorage.getItem("tenant") || "";
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/me`,
          {
            withCredentials: true,
            headers: { "X-Tenant": tenant },
          }
        );

        if (res.data?.success && res.data.user) {
          sessionStorage.setItem("user", JSON.stringify(res.data.user));
          setRole(res.data.user.role);

          const u = res.data.user;
          const name = u.full_name || u.fullName || u.username || u.email || u.name || '';
          setUserDisplay(name);

          const tenantNow = localStorage.getItem("tenant") || "";
          setTenantDisplay(tenantNow);
        }
      } catch (err) {
        console.warn("Session restore failed:", err);
      }
    };

    restoreSession();
  }, []);

  const isHR = role === "hr";

  const openMenu = (event) => setAnchorEl(event.currentTarget);
  const closeMenu = () => {
    setAnchorEl(null);
    setSetupOpen(false);
  };

  const go = (path) => {
    navigate(path);
    closeMenu();
  };

  return (
    <AppBar position="static" sx={{ bgcolor: "#2e7d32" }}>
      <Toolbar>
        <IconButton
          color="inherit"
          edge="start"
          onClick={openMenu}
          sx={{ mr: 2 }}
        >
          <MenuIcon />
        </IconButton>

        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          OnTime Web Admin
        </Typography>

        {/* User / Tenant */}
        <Typography
          variant="body1"
          sx={{
            mr: 2,
            display: { xs: 'none', sm: 'block' },
            opacity: 0.95,
            fontWeight: 550,
            whiteSpace: 'nowrap'
          }}
        >
          {userDisplay ? `User: ${userDisplay}` : 'User: -'}
          {`  |  Customer: ${tenantDisplay || '-'}`}
        </Typography>

        {/* Back Button */}
        <IconButton color="inherit" onClick={() => navigate(-1)}>
          <HistoryIcon />
        </IconButton>

        {/* Home Button */}
        <IconButton color="inherit" onClick={() => navigate('/')}>
          <HomeIcon />
        </IconButton>

        {isHR && (
          <IconButton color="inherit" onClick={() => navigate('/approval/inbox')}>
            <Badge badgeContent={pendingCount} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>
        )}

        <IconButton color="inherit" onClick={handleLogout}>
          <LogoutIcon />
        </IconButton>
      </Toolbar>

      {/* Dropdown Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
        <MenuItem onClick={() => setSetupOpen(!setupOpen)}>
          <ListItemText primary="Setup" />
          {setupOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </MenuItem>
        {setupOpen && (
          <>
            <MenuItem onClick={() => go("/setup/locations")}>
              <ListItemText primary="➤ Locations" />
            </MenuItem>
            <MenuItem onClick={() => go("/setup/beacons")}>
              <ListItemText primary="➤ Beacons" />
            </MenuItem>
            <MenuItem onClick={() => go("/setup/workflow")}>
              <ListItemText primary="➤ Approval Workflow" />
            </MenuItem>
            <MenuItem onClick={() => go("/setup/staff-approvers")}>
              <ListItemText primary="➤ Staff Approvers" />
            </MenuItem>
          </>
        )}
        <Divider />
        <MenuItem onClick={() => go("/")}>Dashboard</MenuItem>
        <Divider />
        {isHR && (
          <MenuItem onClick={() => go("/staff")}>Staff Management</MenuItem>
        )}
        <Divider />
        {isHR && (
          <MenuItem onClick={() => go("/attendance")}>Attendance Records</MenuItem>
        )}
        <Divider />
        <MenuItem
          onClick={() => {
            closeMenu();
            navigate("/emergency/hot");
          }}
        >
          <ListItemText primary="Hot Events" />
          <ListItemIcon><WarningIcon fontSize="small" /></ListItemIcon>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            closeMenu();
            navigate("/emergency/hot/history");
          }}
        >
          <ListItemText primary="Emergency History" />
          <ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon>
        </MenuItem>
        <Divider />
        {isHR && (
          <MenuItem onClick={() => go("/approval/inbox")}>
            <ListItemText primary="Approval Center" />
          </MenuItem>
        )}
        <Divider />
        {isHR && (
          <MenuItem onClick={() => go("/reports/manual")}>
            <ListItemText primary="Manual Requests Report" />
          </MenuItem>
        )}
        <Divider />
        {isHR && (
          <MenuItem onClick={() => go("/export/t2hr")}>
            <ListItemText primary="Export T2HR Data" />
          </MenuItem>
        )}

        {/* Future items */}
        {/* <MenuItem onClick={() => go("/emergency")}>Emergency Dashboard</MenuItem>
        <MenuItem onClick={() => go("/devices")}>Device Approvals</MenuItem> */}
      </Menu>
    </AppBar>
  );
};

export default AppHeader;